import { useState } from 'react';
import {
  X,
  CreditCard,
  Calendar,
  DollarSign,
  Info,
  AlertCircle,
  Trash2,
  Car,
  Clock,
  Loader2,
} from 'lucide-react';
import { doc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { auth, db } from '../../shared/firebase';
import { ListRow } from '../../shared/ui';

export default function DriverPreferencesSheet({ open, onClose, driver }) {
  const { user, signOut } = useAuth();
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const onSignOut = async () => {
    setBusy(true);
    try {
      await signOut();
    } catch (e) {
      setBusy(false);
    }
  };

  const onDeleteAccount = async () => {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      try {
        await deleteDoc(doc(db, 'users', user.uid));
      } catch (e) {
        console.warn('user doc delete failed:', e);
      }
      await auth.currentUser.delete();
      // Auth state change routes to /login automatically.
    } catch (e) {
      setBusy(false);
      setConfirmDelete(false);
      if (e.code === 'auth/requires-recent-login') {
        setError(
          'For security reasons, please sign out and sign back in before deleting your account.'
        );
      } else {
        setError(
          'Failed to delete account. Please try again or contact support.'
        );
      }
    }
  };

  const totalEarnings = driver?.totalEarnings ?? driver?.raw?.earnings ?? 0;
  const onTimeRate = driver?.onTimeRate ?? 0;
  const onTimePct = Math.round(onTimeRate <= 1 ? onTimeRate * 100 : onTimeRate);

  return (
    <div
      className={`fixed inset-0 z-50 ${open ? 'block' : 'hidden'}`}
      onClick={onClose}
    >
      <div
        className="fixed inset-0 bg-black/40"
        onClick={onClose}
      />
      <div
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 active:bg-gray-200"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-6">
          {/* Earnings Summary */}
          <div>
            <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Earnings Summary
            </p>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100">
              <ListRow
                icon={DollarSign}
                label="Total Earnings"
                value={`$${totalEarnings.toFixed(2)}`}
                chevron={false}
              />
              <ListRow
                icon={Clock}
                label="On-Time Rate"
                value={`${onTimePct}%`}
                chevron={false}
              />
              <ListRow
                icon={Car}
                label="Total Deliveries"
                value={driver?.totalDeliveries || 0}
                chevron={false}
              />
            </div>
          </div>

          {/* Payment & Commission Section */}
          <div>
            <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Payment & Commission
            </p>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100">
              <ListRow
                icon={DollarSign}
                label="Commission Structure"
                value="90% of delivery fee"
                chevron={false}
              />
              <ListRow
                icon={Calendar}
                label="Payment Schedule"
                value="Daily payouts"
                onClick={() => {}}
              />
              <ListRow
                icon={CreditCard}
                label="Payment Methods"
                value="Bank transfer"
                chevron={false}
              />
            </div>
          </div>

          {/* Sign Out */}
          <div>
            <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Account
            </p>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100">
              <ListRow
                icon={AlertCircle}
                label="Sign Out"
                onClick={() => setConfirmSignOut(true)}
              />
            </div>
          </div>

          {/* Account Management */}
          <div>
            <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Account Management
            </p>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100">
              <ListRow
                icon={Trash2}
                label="Delete Account"
                destructive
                onClick={() => setConfirmDelete(true)}
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4">
              <div className="flex gap-3">
                <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">
                  {error}
                </div>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
            <div className="flex gap-3">
              <Info size={18} className="text-[#FF6B35] shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-semibold text-gray-900 mb-1">Payment Information</p>
                <p className="text-xs">
                  You earn 90% of each delivery fee. Your first payout takes 2 days;
                  after that, payouts are processed daily with no skipped days.
                  You must have a valid bank account linked to receive payouts.
                  Contact support if you have questions about your payment schedule or earnings.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sign Out Confirmation */}
        {confirmSignOut && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl max-w-sm w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Sign Out</h3>
              <p className="text-sm text-gray-600 mb-6">Are you sure you want to sign out?</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmSignOut(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold text-sm active:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onSignOut}
                  disabled={busy}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-[#FF6B35] text-white font-semibold text-sm active:bg-[#e55a2b] disabled:opacity-50"
                >
                  {busy ? 'Signing out...' : 'Sign Out'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Account Confirmation */}
        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl max-w-sm w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Account</h3>
              <p className="text-sm text-gray-600 mb-6">
                This action cannot be undone. Your account and all associated data including delivery history, earnings, and profile data will be permanently deleted.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold text-sm active:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onDeleteAccount}
                  disabled={busy}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-sm active:bg-red-600 disabled:opacity-50"
                >
                  {busy ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Deleting...
                    </span>
                  ) : (
                    'Delete Account'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}